import {Exclude, Expose, instanceToPlain, plainToInstance, Transform, Type} from "class-transformer";
import * as t from "io-ts";
import {validateJSON} from "../web/validation";
import {CargoSubtype, cargoSubtype} from "./cargo-subtype";
import {ContainerLevelHistoryType, containerLevelHistoryTypes} from "./container-level-history";
import {LevelArrayValidator, LevelEntity, LevelValidator} from "./level";
import {ContainerFillEntity, ContainerFillValidator} from "./container-fill";
import {ContainerTotalFillEntity, ContainerTotalFillValidator} from "./container-total-fill";
import {ContainerConfigurable} from "./container";
import {RestrictionArrayValidator, RestrictionEntity, restrictionType} from "./restriction";
import {MeterArrayValidator, MeterEntity, meterType} from "./meter";
import {transportCategory, TransportCategory} from "./transport-category";
import {MessageArrayValidator, MessageEntity} from "./message";
import {ScadaMode, toScadaMode} from "./scada-mode";
import {getDatabaseCodeTransformer} from "./transfomers/transformers";
import {TagAssignmentCodec, TagAssignmentEntity} from "./tag-assignment";

export const ContainerStatusConnectionMetadataCodec = t.partial({
  source: t.keyof({
    DIRECT_CONNECTION: null,
    LOCAL_TENANT_FALLBACK: null,
  }),
  directConnectionAvailable: t.boolean,
});

export const ContainerStatusMeasurementsMetadataCodec = t.partial({
  available: t.boolean,
  included: t.boolean,
  omissionReason: t.keyof({
    NOT_REQUESTED: null,
    CONNECTION_UNAVAILABLE: null,
    ENRICHMENT_FAILED: null,
  }),
});

export const ContainerStatusMetadataCodec = t.partial({
  connection: ContainerStatusConnectionMetadataCodec,
  measurements: ContainerStatusMeasurementsMetadataCodec,
});

export type ContainerStatusConnectionSource = t.TypeOf<typeof ContainerStatusConnectionMetadataCodec>["source"];
export type ContainerStatusMeasurementOmissionReason = t.TypeOf<typeof ContainerStatusMeasurementsMetadataCodec>["omissionReason"];

@Exclude()
export class ContainerStatusConnectionMetadataEntity {
  @Expose() source?: ContainerStatusConnectionSource;
  @Expose() directConnectionAvailable?: boolean;
}

@Exclude()
export class ContainerStatusMeasurementsMetadataEntity {
  @Expose() available?: boolean;
  @Expose() included?: boolean;
  @Expose() omissionReason?: ContainerStatusMeasurementOmissionReason;
}

@Exclude()
export class ContainerStatusMetadataEntity {
  @Expose() @Type(() => ContainerStatusConnectionMetadataEntity) connection?: ContainerStatusConnectionMetadataEntity;
  @Expose() @Type(() => ContainerStatusMeasurementsMetadataEntity) measurements?: ContainerStatusMeasurementsMetadataEntity;
}

export const ContainerStatusCodec = t.intersection([
  t.type({
    containerId: t.string,
    containerName: t.string,
  }),
  t.partial({
    containerDispatchIgnoreMaxNonPrimaryVolume: t.boolean,
    containerDoNotDispatch: t.boolean,
    containerClearDoNotDispatchScheduledDttm: t.number,
    containerClearDoNotDispatchById: t.string,
    containerClearDoNotDispatchNote: t.string,
    containerClearDoNotDispatchDttm: t.number,
    containerDoNotUse: t.boolean,
    containerClearDoNotUseScheduledDttm: t.number,
    containerClearDoNotUseById: t.string,
    containerClearDoNotUseNote: t.string,
    containerClearDoNotUseDttm: t.number,
    containerLactMeterAttached: t.boolean,
    containerHeight: t.number,
    containerVolume: t.number,
    containerClusterId: t.string,
    containerScadaMode: t.string,
    containerScadaModeReason: t.string,
    containerMeters: MeterArrayValidator,
    containerMessages: MessageArrayValidator,
    containerTags: t.array(TagAssignmentCodec),
    siteId: t.string,
    siteName: t.string,
    siteOperatingAreaId: t.string,
    siteOperatingAreaName: t.string,
    siteRouteGroupId: t.string,
    siteRouteGroupName: t.string,
    siteRouteId: t.string,
    siteRouteName: t.string,
    siteTransportViaPipeline: t.boolean,
    siteTransportOilViaPipeline: t.boolean,
    siteCompanyId: t.string,
    siteCompanyName: t.string,
    siteAvailableLoadVolume: t.number,
    assignedCompanyId: t.string,
    assignedCompanyName: t.string,
    restrictions: RestrictionArrayValidator,
    latestDispatchId: t.string,
    latestDispatchDttm: t.number,
    latestPickupId: t.string,
    latestPickupDttm: t.number,
    latestDeliveryId: t.string,
    latestDeliveryDttm: t.number,
    waterMeterLevel: LevelValidator,
    waterDriverEnteredLevel: LevelValidator,
    waterFieldEnteredLevel: LevelValidator,
    waterMeterFill: ContainerFillValidator,
    waterMeterHistory: LevelArrayValidator,
    oilMeterLevel: LevelValidator,
    oilDriverEnteredLevel: LevelValidator,
    oilFieldEnteredLevel: LevelValidator,
    oilMeterFill: ContainerFillValidator,
    oilMeterHistory: LevelArrayValidator,
    chemicalMeterLevel: LevelValidator,
    chemicalDriverEnteredLevel: LevelValidator,
    chemicalFieldEnteredLevel: LevelValidator,
    wasteMeterLevel: LevelValidator,
    wasteDriverEnteredLevel: LevelValidator,
    wasteFieldEnteredLevel: LevelValidator,
    meterFill: ContainerTotalFillValidator,
    metadata: ContainerStatusMetadataCodec,
  }),
]);

export const ContainerStatusArrayCodec = t.array(ContainerStatusCodec);

export type ContainerStatus = t.TypeOf<typeof ContainerStatusCodec>;
export type ContainerStatusArray = t.TypeOf<typeof ContainerStatusArrayCodec>;

/**
 * A container.
 */
@Exclude()
export class ContainerStatusEntity implements ContainerConfigurable {
  @Expose() containerId!: string;
  @Expose() containerName!: string;
  @Expose() containerDispatchIgnoreMaxNonPrimaryVolume?: boolean;
  @Expose() containerDoNotDispatch?: boolean;
  @Expose() containerClearDoNotDispatchScheduledDttm?: number
  @Expose() containerClearDoNotDispatchById?: string
  @Expose() containerClearDoNotDispatchNote?: string
  @Expose() containerClearDoNotDispatchDttm?: number
  @Expose() containerDoNotUse?: boolean;
  @Expose() containerClearDoNotUseScheduledDttm?: number;
  @Expose() containerClearDoNotUseById?: string
  @Expose() containerClearDoNotUseNote?: string
  @Expose() containerClearDoNotUseDttm?: number
  @Expose() containerHeight?: number
  @Expose() containerVolume?: number
  @Expose() containerClusterId?: string
  @Expose() @Transform(getDatabaseCodeTransformer(toScadaMode)) containerScadaMode?: ScadaMode
  @Expose() containerScadaModeReason?: string
  @Expose() @Type(() => MeterEntity) containerMeters?: MeterEntity[];
  @Expose() @Type(() => MessageEntity) containerMessages?: MessageEntity[];
  @Expose() @Type(() => TagAssignmentEntity) containerTags?: TagAssignmentEntity[];
  @Expose() siteId?: string
  @Expose() siteName?: string
  @Expose() siteOperatingAreaId?: string
  @Expose() siteOperatingAreaName?: string
  @Expose() siteRouteGroupId?: string
  @Expose() siteRouteGroupName?: string
  @Expose() siteRouteId?: string
  @Expose() siteRouteName?: string
  @Expose() siteTransportViaPipeline?: boolean
  @Expose() siteTransportOilViaPipeline?: boolean
  @Expose() siteCompanyId?: string
  @Expose() siteCompanyName?: string
  @Expose() siteAvailableLoadVolume?: number
  @Expose() assignedCompanyId?: string
  @Expose() assignedCompanyName?: string
  @Expose() @Type(() => RestrictionEntity) restrictions?: RestrictionEntity[];
  @Expose() latestDispatchId?: string
  @Expose() latestDispatchDttm?: number
  @Expose() latestPickupId?: string
  @Expose() latestPickupDttm?: number
  @Expose() latestDeliveryId?: string
  @Expose() latestDeliveryDttm?: number
  @Expose() @Type(() => LevelEntity) waterMeterLevel?: LevelEntity;
  @Expose() @Type(() => LevelEntity) waterDriverEnteredLevel?: LevelEntity;
  @Expose() @Type(() => LevelEntity) waterFieldEnteredLevel?: LevelEntity;
  @Expose() @Type(() => ContainerFillEntity) waterMeterFill?: ContainerFillEntity;
  @Expose() @Type(() => LevelEntity) waterMeterHistory?: LevelEntity[];
  @Expose() @Type(() => LevelEntity) oilMeterLevel?: LevelEntity;
  @Expose() @Type(() => LevelEntity) oilDriverEnteredLevel?: LevelEntity;
  @Expose() @Type(() => LevelEntity) oilFieldEnteredLevel?: LevelEntity;
  @Expose() @Type(() => ContainerFillEntity) oilMeterFill?: ContainerFillEntity;
  @Expose() @Type(() => LevelEntity) oilMeterHistory?: LevelEntity[];
  @Expose() @Type(() => LevelEntity) chemicalMeterLevel?: LevelEntity;
  @Expose() @Type(() => LevelEntity) chemicalDriverEnteredLevel?: LevelEntity;
  @Expose() @Type(() => LevelEntity) chemicalFieldEnteredLevel?: LevelEntity;
  @Expose() @Type(() => LevelEntity) wasteMeterLevel?: LevelEntity;
  @Expose() @Type(() => LevelEntity) wasteDriverEnteredLevel?: LevelEntity;
  @Expose() @Type(() => LevelEntity) wasteFieldEnteredLevel?: LevelEntity;
  @Expose() @Type(() => ContainerTotalFillEntity) meterFill?: ContainerTotalFillEntity;
  @Expose() @Type(() => ContainerStatusMetadataEntity) metadata?: ContainerStatusMetadataEntity;

  /**
   * Constructor for ContainerStatusEntity.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(containerStatus: ContainerStatus): ContainerStatusEntity {
    return plainToInstance(
      ContainerStatusEntity,
      containerStatus,
      {
        excludeExtraneousValues: true,
      }
    );
  }

  toTypeValue(): Promise<ContainerStatus> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(ContainerStatusCodec, object);
  }

  getHistory = (
    type: CargoSubtype,
    levelType: ContainerLevelHistoryType
  ): LevelEntity[] | undefined => {
    switch (type) {
      case cargoSubtype.water:
        switch (levelType) {
          case containerLevelHistoryTypes.meter:
            return this.waterMeterHistory;
          default:
            return undefined;
        }
      case cargoSubtype.oil:
        switch (levelType) {
          case containerLevelHistoryTypes.meter:
            return this.oilMeterHistory;
          default:
            return undefined
        }
      default:
        return undefined
    }
  }

  getFill = (
    type: CargoSubtype,
    levelType: ContainerLevelHistoryType
  ): ContainerFillEntity | undefined => {
    switch (type) {
      case cargoSubtype.water:
        switch (levelType) {
          case containerLevelHistoryTypes.meter:
            return this.waterMeterFill;
          default:
            return undefined;
        }
      case cargoSubtype.oil:
        switch (levelType) {
          case containerLevelHistoryTypes.meter:
            return this.oilMeterFill;
          default:
            return undefined
        }
      default:
        return undefined
    }
  }

  getLevel = (
    type: CargoSubtype,
    levelType: ContainerLevelHistoryType
  ): LevelEntity | undefined => {
    switch (type) {
      case cargoSubtype.water:
        switch (levelType) {
          case containerLevelHistoryTypes.meter:
            return this.waterMeterLevel;
          case containerLevelHistoryTypes.driverEntered:
            return this.waterDriverEnteredLevel;
          case containerLevelHistoryTypes.fieldEntered:
            return this.waterFieldEnteredLevel;
        }
        break;
      case cargoSubtype.oil:
        switch (levelType) {
          case containerLevelHistoryTypes.meter:
            return this.oilMeterLevel;
          case containerLevelHistoryTypes.driverEntered:
            return this.oilDriverEnteredLevel;
          case containerLevelHistoryTypes.fieldEntered:
            return this.oilFieldEnteredLevel;
        }
        break;
      case cargoSubtype.chemicals:
        switch (levelType) {
          case containerLevelHistoryTypes.meter:
            return this.chemicalMeterLevel;
          case containerLevelHistoryTypes.driverEntered:
            return this.chemicalDriverEnteredLevel;
          case containerLevelHistoryTypes.fieldEntered:
            return this.chemicalFieldEnteredLevel;
        }
        break;
      case cargoSubtype.waste:
        switch (levelType) {
          case containerLevelHistoryTypes.meter:
            return this.wasteMeterLevel;
          case containerLevelHistoryTypes.driverEntered:
            return this.wasteDriverEnteredLevel;
          case containerLevelHistoryTypes.fieldEntered:
            return this.wasteFieldEnteredLevel;
        }
        break;
    }

    return undefined;
  };

  get inRejectionRestriction(): boolean {
    if (this.restrictions) {
      return this.restrictions.findIndex(r => r.type === restrictionType.rejection) >= 0
    }

    return false
  }

  isPiped(category: TransportCategory | undefined): boolean | undefined {
    if (category === transportCategory.oil) {
      return this.siteTransportOilViaPipeline
    } else if (category === transportCategory.water) {
      return this.siteTransportViaPipeline
    } else {
      return this.siteTransportViaPipeline || this.siteTransportOilViaPipeline
    }
  }

  get name(): string | undefined {
    return this.containerName
  }

  get dispatchIgnoreMaxNonPrimaryVolume(): boolean | undefined {
    return this.containerDispatchIgnoreMaxNonPrimaryVolume
  }

  get doNotDispatch(): boolean | undefined {
    return this.containerDoNotDispatch
  }

  get clearDoNotDispatchScheduledDttm(): number | undefined {
    return this.containerClearDoNotDispatchScheduledDttm
  }

  get clearDoNotDispatchById(): string | undefined {
    return this.containerClearDoNotDispatchById
  }

  get clearDoNotDispatchNote(): string | undefined {
    return this.containerClearDoNotDispatchNote
  }

  get clearDoNotDispatchDttm(): number | undefined {
    return this.containerClearDoNotDispatchDttm
  }

  get doNotUse(): boolean | undefined {
    return this.containerDoNotUse
  }

  get clearDoNotUseScheduledDttm(): number | undefined {
    return this.containerClearDoNotUseScheduledDttm
  }

  get clearDoNotUseById(): string | undefined {
    return this.containerClearDoNotUseById
  }

  get clearDoNotUseNote(): string | undefined {
    return this.containerClearDoNotUseNote
  }

  get clearDoNotUseDttm(): number | undefined {
    return this.containerClearDoNotUseDttm
  }

  get lactMeters(): MeterEntity[] | undefined {
    return this.containerMeters?.filter(m => m.type === meterType.lact)
  }

  get scadaMode(): ScadaMode | undefined {
    return this.containerScadaMode
  }

  get scadaModeReason(): string | undefined {
    return this.containerScadaModeReason
  }

  get id(): string {
    return this.containerId
  }
}
